/**
 * Typed client for /api/feedback (backend/feedback/routes.py).
 *
 * Two surfaces: the in-app Feedback button POSTs multipart/form-data (text
 * only — the client no longer sends a screenshot, see FeedbackButton.tsx);
 * the admin review page lists + patches status as JSON. The backend route
 * still accepts an optional screenshot upload and older rows may carry one,
 * so `screenshot_path` / `screenshotUrl()` stay in this client for the admin
 * review page. Mirrors authAdminApi.ts envelope handling — same-origin
 * fetch, FastAPI {"detail": ...} surfaced as Error.message, 401 emits the
 * session-expired event so a stale session routes back to Login.
 */
import { emitSessionExpired } from './authEvents'

export type FeedbackKind = 'feedback' | 'bug' | 'feature'
export type FeedbackStatus = 'open' | 'triaged' | 'done' | 'wontfix'

export type FeatureRequest = {
  id: number
  created_at: string | null
  source: string
  reporter_email: string | null
  reporter_name: string | null
  kind: string
  body: string
  page_url: string | null
  route: string | null
  viewport: string | null
  user_agent: string | null
  screenshot_path: string | null
  status: FeedbackStatus
  resolved_at: string | null
  resolved_by: string | null
}

export type FeedbackSubmit = {
  body: string
  kind: FeedbackKind
  page_url?: string
  route?: string
  viewport?: string
  user_agent?: string
}

async function jsonReq<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  })
  if (!res.ok) {
    if (res.status === 401) emitSessionExpired()
    let detail = `${res.status} ${res.statusText}`
    try {
      const body = await res.json()
      if (typeof body?.detail === 'string') detail = body.detail
    } catch { /* keep the status-line message */ }
    throw new Error(detail)
  }
  return res.json()
}

export const feedbackApi = {
  /** POST /api/feedback — multipart. Any authenticated user may file. */
  async submit(data: FeedbackSubmit): Promise<{ id: number }> {
    const form = new FormData()
    form.append('body', data.body)
    form.append('kind', data.kind)
    if (data.page_url) form.append('page_url', data.page_url)
    if (data.route) form.append('route', data.route)
    if (data.viewport) form.append('viewport', data.viewport)
    if (data.user_agent) form.append('user_agent', data.user_agent)
    // No screenshot field — see FeedbackButton.tsx doc comment. Still
    // multipart (not JSON) so this stays a drop-in match for the backend
    // route's form contract.
    // No Content-Type header — the browser sets the multipart boundary.
    const res = await fetch('/api/feedback', { method: 'POST', body: form })
    if (!res.ok) {
      if (res.status === 401) emitSessionExpired()
      let detail = `${res.status} ${res.statusText}`
      try {
        const body = await res.json()
        if (typeof body?.detail === 'string') detail = body.detail
      } catch { /* keep status-line */ }
      throw new Error(detail)
    }
    return res.json()
  },

  /** GET /api/feedback — admin-only review list (newest first). */
  list: (status?: FeedbackStatus) =>
    jsonReq<{ feature_requests: FeatureRequest[] }>(
      status ? `/api/feedback?status=${encodeURIComponent(status)}` : '/api/feedback',
    ),

  /** PATCH /api/feedback/{id} — admin-only status triage. */
  setStatus: (id: number, status: FeedbackStatus) =>
    jsonReq<{ id: number; status: FeedbackStatus }>(`/api/feedback/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),

  /** Admin-only screenshot URL (admin cookie authorizes the <img src>). */
  screenshotUrl: (id: number) => `/api/feedback/${id}/screenshot`,
}
